// Phase 4C.1 — Memory control surface.
//
// Everything the privacy model promises is operable here: consent, list, edit,
// forget, clear all, export. Memory is OFF until the visitor turns it on.

import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Brain, Download, Loader2, Pencil, Trash2, TriangleAlert } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  clearMemoriesFn,
  editMemoryFn,
  exportMemoriesFn,
  forgetMemoryFn,
  getMemoryState,
  saveMemoryFn,
  setMemoryConsent,
} from "@/lib/memory.functions";
import type { PublicMemory } from "@/lib/intelligence/memory/types";

export const Route = createFileRoute("/_authenticated/settings/memory")({
  head: () => ({
    meta: [
      { title: "Memory · KA Intelligence · KAfrequency" },
      {
        name: "description",
        content:
          "Control what KA Intelligence remembers about you: turn memory on or off, review every saved memory, edit it, forget it, or export it.",
      },
      { property: "og:title", content: "Memory · KA Intelligence" },
      {
        property: "og:description",
        content: "Review, edit, forget and export everything KA Intelligence has saved for you.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: MemorySettingsPage,
});

function MemorySettingsPage() {
  const load = useServerFn(getMemoryState);
  const consent = useServerFn(setMemoryConsent);
  const save = useServerFn(saveMemoryFn);
  const edit = useServerFn(editMemoryFn);
  const forget = useServerFn(forgetMemoryFn);
  const clearAll = useServerFn(clearMemoriesFn);
  const exportAll = useServerFn(exportMemoriesFn);

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [memories, setMemories] = useState<PublicMemory[]>([]);
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const state = await load({});
    setEnabled(state.settings.persistentMemoryEnabled);
    setMemories(state.memories);
    setLoading(false);
  }, [load]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const toggle = async (next: boolean) => {
    setBusy(true);
    setNotice(null);
    await consent({ data: { enabled: next } });
    await refresh();
    setBusy(false);
    setNotice(
      next
        ? "Memory is on. Only what you explicitly save is kept."
        : "Memory is off. Nothing new will be saved; existing memories stay until you remove them.",
    );
  };

  const addMemory = async () => {
    if (!draft.trim()) return;
    setBusy(true);
    setNotice(null);
    const result = await save({ data: { text: draft.trim(), confirmed: true } });
    if (!result.ok) setNotice(result.message);
    else setDraft("");
    await refresh();
    setBusy(false);
  };

  const commitEdit = async () => {
    if (!editing) return;
    setBusy(true);
    const result = await edit({ data: { memoryId: editing.id, text: editing.text.trim() } });
    if (!result.ok) setNotice(result.message);
    setEditing(null);
    await refresh();
    setBusy(false);
  };

  const doExport = async () => {
    setBusy(true);
    const payload = await exportAll({});
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "ka-intelligence-memory.json";
    a.click();
    URL.revokeObjectURL(url);
    setBusy(false);
    setNotice("Your memory export has been downloaded.");
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6 px-4 py-8">
      <header>
        <h1 className="flex items-center gap-2 font-serif text-3xl text-gold-luminous">
          <Brain size={24} aria-hidden="true" /> Memory
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          KA Intelligence remembers nothing by default. Turn memory on and it will keep only what you
          explicitly ask it to keep — never credentials, payment details or anything it merely
          inferred about you.
        </p>
      </header>

      {notice ? (
        <p
          role="status"
          className="flex items-start gap-2 rounded-md border border-border bg-muted/40 p-3 text-sm"
        >
          <TriangleAlert size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
          <span>{notice}</span>
        </p>
      ) : null}

      <Card className="flex items-center justify-between gap-4 p-4">
        <div>
          <p className="font-medium">Persistent memory</p>
          <p className="text-sm text-muted-foreground">
            Off by default. Signed-out visitors are never stored on our servers at all.
          </p>
        </div>
        <Switch
          checked={enabled}
          disabled={busy || loading}
          onCheckedChange={(v) => void toggle(v)}
          aria-label="Enable persistent memory"
        />
      </Card>

      <Card className="space-y-3 p-4">
        <h2 className="font-medium">Add a memory</h2>
        <Textarea
          value={draft}
          maxLength={500}
          disabled={!enabled || busy}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="e.g. I practise at 5am and prefer short, direct answers."
          aria-label="New memory"
        />
        <div className="flex justify-end">
          <Button onClick={() => void addMemory()} disabled={!enabled || busy || !draft.trim()}>
            {busy ? <Loader2 className="animate-spin" size={16} /> : null} Save memory
          </Button>
        </div>
        {!enabled ? (
          <p className="text-xs text-muted-foreground">Turn memory on to save anything.</p>
        ) : null}
      </Card>

      <section aria-labelledby="saved-memories" className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 id="saved-memories" className="font-medium">
            Saved memories ({memories.length})
          </h2>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => void doExport()} disabled={busy}>
              <Download size={14} /> Export
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={busy || memories.length === 0}
              onClick={async () => {
                setBusy(true);
                await clearAll({});
                await refresh();
                setBusy(false);
                setNotice("All memories were deleted.");
              }}
            >
              <Trash2 size={14} /> Clear all
            </Button>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading your memories…</p>
        ) : memories.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nothing is saved. This is the default state.
          </p>
        ) : (
          <ul className="space-y-2">
            {memories.map((m) => (
              <li key={m.id}>
                <Card className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between">
                  {editing?.id === m.id ? (
                    <div className="flex w-full flex-col gap-2 sm:flex-row">
                      <Input
                        value={editing.text}
                        maxLength={500}
                        aria-label="Edit memory"
                        onChange={(e) => setEditing({ id: m.id, text: e.target.value })}
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => void commitEdit()} disabled={busy}>
                          Save
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="min-w-0">
                        <p className="text-sm">{m.displayText}</p>
                        <p className="text-xs text-muted-foreground">
                          {m.category} · {m.scope} · updated{" "}
                          {new Date(m.updatedAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label={`Edit memory: ${m.displayText}`}
                          onClick={() => setEditing({ id: m.id, text: m.displayText })}
                        >
                          <Pencil size={14} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label={`Forget memory: ${m.displayText}`}
                          disabled={busy}
                          onClick={async () => {
                            setBusy(true);
                            await forget({ data: { memoryId: m.id } });
                            await refresh();
                            setBusy(false);
                          }}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </>
                  )}
                </Card>
              </li>
            ))}
          </ul>
        )}
      </section>

      <p className="text-xs text-muted-foreground">
        Memory is your personal context only. It never overrides the temple's canonical sources: for
        any fact about releases, prices, dates or teachings, governed evidence always wins.
      </p>
    </div>
  );
}
